import React, { useMemo, lazy, Suspense, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";

import { useTypedDispatch, useTypedSelector } from "../../../../redux/store";
import getCompanies from "../../../../redux/services/Company/getCompanies";
import useActiveTab from "../../../../hooks/useActiveTab";
import { hasAccess } from "../../../../access";
import { TabOptions } from "../../../../types";
import BasicPageLayout from "../../../../components/BasicPageLayout";
import {
	StyledRow,
	SuspenseLoader,
	SideTab,
} from "../../../../components/common";

import { TabKeys, TabAccessNames } from "./constants/access";

const LazyExecutor = lazy<React.FC<MobileAppsSettingsPage.Props>>(async () => {
	const elem = await import(
		"./Executor" /* webpackChunkName: "setting-mobile-app-page-executor" */
	);
	return { default: elem.default };
});

const LazyClient = lazy<React.FC<MobileAppsSettingsPage.Props>>(async () => {
	const elem = await import(
		"./Client" /* webpackChunkName: "setting-mobile-app-page-client" */
	);
	return { default: elem.default };
});

const MobileAppsSettingsPage: React.FC = () => {
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();

	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	useLayoutEffect(() => {
		if (dispatch) {
			dispatch(getCompanies());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	const tabs: TabOptions.Array = useMemo(
		() =>
			[
				{
					key: TabKeys.EXECUTOR,
					label: t("settings.mobileApps.executor.title"),
					value: {
						render: () => (
							<Suspense
								fallback={
									<StyledRow
										position="absolute"
										top="50%"
										left="50%"
									>
										<SuspenseLoader />
									</StyledRow>
								}
							>
								<LazyExecutor />
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.EXECUTOR],
				},
				{
					key: TabKeys.CLIENT,
					label: t("settings.mobileApps.client.title"),
					value: {
						render() {
							return (
								<Suspense
									fallback={
										<StyledRow
											position="absolute"
											top="50%"
											left="50%"
										>
											<SuspenseLoader />
										</StyledRow>
									}
								>
									<LazyClient />
								</Suspense>
							);
						},
					},
					accessName: TabAccessNames[TabKeys.CLIENT],
				},
			].filter((btn) => hasAccess(btn.accessName, personalRole)),
		[t, personalRole],
	);

	const { activeKey, activeTab, setActiveKey } = useActiveTab(tabs);

	return (
		<Suspense
			fallback={
				<StyledRow position="absolute" top="50%" left="50%">
					<SuspenseLoader />
				</StyledRow>
			}
		>
			<BasicPageLayout
				navigation={
					<SideTab
						value={activeKey}
						onChange={setActiveKey}
						options={tabs}
						variant="vertical"
					/>
				}
				content={activeTab.value.render()}
			/>
		</Suspense>
	);
};

declare namespace MobileAppsSettingsPage {
	interface Props {}
}

export default MobileAppsSettingsPage;
